import React, {
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { Checkbox } from '@mui/material';

import ChatContext from '../../context/ChatContext';
import { formatDateForChat } from '../../utils/time';
import MessagingChannelProfilePic from '../ChatUserProfilePicture';
import { variants } from '../Tag';

import {
  ChannelDescription,
  ChannelName,
  ChannelPreviewContainer,
  IconContainer,
  InfoContainer,
  LastMessageContainer,
  LastMessageDate,
  LastMessageText,
  StyledChannelPopoverIcon,
  TextContainer,
  UnreadBadge,
  UnreadText,
  StyledTag,
} from './styles';
import texts from './texts.json';

const ChannelPreview = ({
  listIndex,
  onClick,
  image,
  lastMessage,
  unreadCount,
  name,
  isAdmin,
  isActive,
  shouldAllowActions,
  handleAction,
  isUnread,
  isOnline,
  channel,
  showCheckbox,
  checked,
  checkboxOnChange,
}) => {
  const { sessionStore, authUser } = useContext(ChatContext);

  const {
    claimedBy,
    claimedByUsername,
    isClaimValid,
    isAIFriendEnabled,
  } = channel;

  const showClaim = sessionStore.isCoachAssistant && !!claimedBy && isClaimValid;
  const isSelfClaimed = !!claimedBy && claimedBy === authUser.id;

  return (
    <ChannelPreviewContainer
      onClick={onClick}
      $isActive={isActive}
      $isAdmin={isAdmin}
    >
      {showCheckbox && (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          checked={checked}
          onChange={(e, isChecked) => checkboxOnChange?.(e, isChecked, listIndex)}
          size="small"
        />
      )}
      <MessagingChannelProfilePic image={image} isOnline={isOnline} name={name} />
      <ChannelDescription>
        <TextContainer>
          <ChannelName>
            {name}
            {unreadCount > 0 && (
              <UnreadBadge>
                <UnreadText>{unreadCount}</UnreadText>
              </UnreadBadge>
            )}
            {isUnread && (
              <UnreadBadge />
            )}
          </ChannelName>
          {showClaim && (
            <StyledTag variant={variants.WARNING}>
              {isSelfClaimed ? texts.selfClaimed : format(texts.claimed, { name: claimedByUsername })}
            </StyledTag>
          )}
          {isAIFriendEnabled && (
            <StyledTag variant={variants.SUCCESS}>
              {texts.aiFriend}
            </StyledTag>
          )}
          <LastMessageContainer>
            <LastMessageText>
              {lastMessage.text}
            </LastMessageText>
          </LastMessageContainer>
        </TextContainer>
        <InfoContainer>
          {!!lastMessage.createdAt && (
            <LastMessageDate $isActive={isActive}>
              {formatDateForChat(lastMessage.createdAt, true)}
            </LastMessageDate>
          )}
          {shouldAllowActions && (
            <IconContainer onClick={(event) => handleAction(event, channel)}>
              <StyledChannelPopoverIcon $isAdmin={isAdmin} />
            </IconContainer>
          )}
        </InfoContainer>
      </ChannelDescription>
    </ChannelPreviewContainer>
  );
};

ChannelPreview.propTypes = {
  listIndex: PropTypes.number.isRequired,
  onClick: PropTypes.func,
  image: PropTypes.string,
  lastMessage: PropTypes.shape({
    text: PropTypes.string,
    createdAt: PropTypes.object,
  }),
  unreadCount: PropTypes.number,
  name: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool,
  isActive: PropTypes.bool,
  shouldAllowActions: PropTypes.bool,
  handleAction: PropTypes.func,
  isUnread: PropTypes.bool,
  isOnline: PropTypes.bool,
  channel: PropTypes.object,
  showCheckbox: PropTypes.bool,
  checked: PropTypes.bool,
  checkboxOnChange: PropTypes.func,
};

ChannelPreview.defaultProps = {
  onClick: () => { },
  image: null,
  lastMessage: {},
  unreadCount: 0,
  isAdmin: false,
  isActive: false,
  shouldAllowActions: false,
  handleAction: () => {},
  isUnread: false,
  isOnline: false,
  channel: {},
  showCheckbox: false,
  checked: false,
  checkboxOnChange: () => {},
};

export default compose(
  observer,
)(ChannelPreview);
